import { useCallback, useRef, useState } from "react";

/** A session that ends this fast, with nothing heard back, reads as a failed connection attempt. */
const QUICK_FAILURE_MS = 8_000;
/** Two quick failures in a row is a pattern, not a blip — offer the fallback. */
const AUTO_OFFER_THRESHOLD = 2;

/**
 * Cheap, frontend-only heuristic for "this voice call is struggling" — no ML,
 * no backend quality scoring (that's a separate, parked work item). Counts
 * consecutive quick, reply-less sessions; two in a row auto-surfaces the
 * voice-trouble fallback. A single normal end (or a session that got a reply)
 * resets the count, so an ordinary short "thanks, bye" call never trips it.
 */
export const useVoiceTroubleSignals = () => {
  const quickFailureStreakRef = useRef(0);

  const [autoOfferToken, setAutoOfferToken] = useState(0);

  /** Call once per ended session. Returns true when the fallback should auto-open. */
  const registerConversationEnd = useCallback(
    (durationMs: number | null, hadAgentReply: boolean): boolean => {
      const wasQuickFailure = durationMs !== null && durationMs < QUICK_FAILURE_MS && !hadAgentReply;

      if (wasQuickFailure) {
        quickFailureStreakRef.current += 1;
      } else {
        quickFailureStreakRef.current = 0;
      }

      if (quickFailureStreakRef.current >= AUTO_OFFER_THRESHOLD) {
        quickFailureStreakRef.current = 0;
        setAutoOfferToken((n) => n + 1);
        return true;
      }
      return false;
    },
    [],
  );

  const reset = useCallback(() => {
    quickFailureStreakRef.current = 0;
  }, []);

  return { registerConversationEnd, autoOfferToken, reset };
};
